
import { IFilterQuery } from "../../../components/boxitFilter/types";
import { clearAuthData } from "../../../store/slices/auth/authSlice";
import { store } from "../../../store/store";
import getFilterString from "../../../utils/getFilterString";
import { baseApi } from "../../helpers/baseApi/origin.baseApi";
import { IDataResponse } from "../types";
import { IPhoto, IStorage, IStorageStats, StorageStatus } from "./types";
import _ from "lodash";

const S3_SIGNED_QUERY_KEYS = new Set([
  "AWSAccessKeyId",
  "Expires",
  "Signature",
  "SecurityToken",
  "X-Amz-Algorithm",
  "X-Amz-Credential",
  "X-Amz-Date",
  "X-Amz-Expires",
  "X-Amz-SignedHeaders",
  "X-Amz-Signature",
  "X-Amz-Security-Token",
]);

const sanitizeS3SignedUrl = (value?: string | null): string => {
  if (value == null || value === "") {
    return "";
  }

  try {
    const parsedUrl = new URL(value);
    let hasChanges = false;

    Array.from(parsedUrl.searchParams.keys()).forEach((key) => {
      if (S3_SIGNED_QUERY_KEYS.has(key) || key.startsWith("X-Amz-")) {
        parsedUrl.searchParams.delete(key);
        hasChanges = true;
      }
    });

    if (!hasChanges) {
      return value;
    }

    const search = parsedUrl.searchParams.toString();
    return `${parsedUrl.origin}${parsedUrl.pathname}${search.length > 0 ? `?${search}` : ""}`;
  } catch {
    return value;
  }
};

const appendPhotoVersion = (
  value: string,
  version?: string | null
): string => {
  if (!value) {
    return value;
  }

  if (!version) {
    return value;
  }

  try {
    const parsedUrl = new URL(value);
    parsedUrl.searchParams.set("v", version);
    return parsedUrl.toString();
  } catch {
    return value;
  }
};


const normalizePhotoUrls = (photo: IPhoto): IPhoto => {
  const version = photo.updatedAt ?? photo.createdAt ?? null;

  return {
    ...photo,
    photo: appendPhotoVersion(sanitizeS3SignedUrl(photo.photo), version),
    thumbnail: appendPhotoVersion(
      sanitizeS3SignedUrl(photo.thumbnail),
      version
    ),
  };
};

const extendedApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    deleteUserCustomField: build.mutation<
      IDataResponse<any>, // Replace with the correct response type
      { id: number } // The ID of the custom field to be deleted
    >({
      query: ({ id }) => ({
        url: `usercustomfields/${id}`, // API endpoint to delete the user custom field
        method: "DELETE",
      }),
      invalidatesTags: ['UserCustomFields'],
      transformResponse: (response: IDataResponse<any>, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getUserCustomFields: build.query<
      IDataResponse<any>, // Replace `any` with the correct response type
      {
        userId: number;
        storageId: number;
      }
    >({
      query: ({ userId, storageId }) => ({
        url: `usercustomfields`,
        method: "GET",
        params: {
          userId,
          storageId,
        },
      }),
      providesTags: ['UserCustomFields'],
      transformResponse: (response: IDataResponse<any>, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    createUserCustomField: build.mutation<
      IDataResponse<any>, // Replace `any` with the correct type if available
      {
        userId: number;
        storageId: number;
        label: string;
        content: string;
        label1?: string;
        label2?: string;
        content1?: string;
        content2?: string;
      }
    >({
      query: (payload) => ({
        url: `usercustomfields`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ['UserCustomFields'],
      transformResponse: (response: IDataResponse<any>, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),

    updateUserCustomField: build.mutation<
      IDataResponse<any>, // Replace `any` with the correct type if available
      {
        id: number;
        data: Partial<{
          userId: number;
          storageId: number;
          label: string;
          content: string;
          label1: string;
          label2: string;
          content1: string;
          content2: string;
        }>;
      }
    >({
      query: ({ id, data }) => ({
        url: `usercustomfields/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ['UserCustomFields'],
      transformResponse: (response: IDataResponse<any>, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),

    getAllStoragesRequest: build.query< IDataResponse<IStorage>, 
    { userId: number;
       filters?: Record<string, string | null>; 
       id?: number; search?: string; 
       status?: StorageStatus[]; 
       filterQueries?: IFilterQuery[]; } 
       >({ query: ({ userId,
         search,
          status,
           filters, 
           filterQueries,
            id }) => 
              ({ url: `storages?userId=${userId}&isPart=false${search && search !== "" ? `&q=${search}` : ``}${status ? status.map((item, index) => `&status[${index}]=${item}`).join("") : ""}${getFilterString(filters!, filterQueries)}&$sort[createdAt]=-1`, method: "get", }), 
            transformResponse: (response: IDataResponse<IStorage>, meta, arg) => response,
             transformErrorResponse: (response, meta, arg) => response, }), 
    // this is being used to fetch storage items (i.e. in the home page)
    getStoragesRequest: build.query<
      IDataResponse<IStorage>,
      {
        userId: number;
        skip: number;
        limit: number;
        filters?: Record<string, string | null>;
        id?: number;
        search?: string;
        status?: StorageStatus[];
        filterQueries?: IFilterQuery[];
      }
    >({
      query: ({
        userId,
        skip,
        limit,
        search,
        status,
        filters,
        filterQueries,
        id,
      }) => ({
        url: `storages?userId=${userId}&$skip=${skip}&$limit=${limit}&isPart=${false}${search && search !== "" ? `&q=${search}` : ``}${status ? status.map((item, index) => `&status[${index}]=${item}`).join("") : ""}${getFilterString(filters!, filterQueries)}&$sort[createdAt]=-1`,
        method: "get",
      }),
      transformResponse: (response: IDataResponse<IStorage>, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    // Add the update storage name endpoint here
    updateStorageName: build.mutation<
      IStorage,
      {
        storageId: number;
        name: string;
      }
    >({
      query: ({ storageId, name }) => ({
        url: `storages/${storageId}`,
        method: "PATCH",
        data: {
          storageId,
          name,
        },
      }),
      transformResponse: (response: IStorage, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getStoragePartsRequest: build.query<
      IDataResponse<IStorage>,
      {
        parentId: number;
      }
    >({
      query: ({ parentId }) => ({
        url: `storages?parentId=${parentId}&$limit=50`,
        method: "get",
      }),
      transformResponse: (response: IDataResponse<IStorage>, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getStoragesPhotosRequest: build.query<
      IDataResponse<IPhoto>,
      {
        storageId: number;
      }
    >({
      query: ({ storageId }) => ({
        url: `photos?storageId=${storageId}`,
        method: "get",
      }),
      providesTags: ["storagePhotos"],
      transformResponse: (response: IDataResponse<IPhoto>, meta, arg) => ({
        ...response,
        data: (response.data ?? []).map((photo) => normalizePhotoUrls(photo)),
      }),
      transformErrorResponse: (response, meta, arg) => response,
    }),
    setStoragesPhotoAsDefaultRequest: build.mutation<
      IPhoto,
      {
        photoId: number;
      }
    >({
      query: ({ photoId }) => ({
        url: `photos/${photoId}`,
        method: "PATCH",
        data: {
          default: true,
        },
      }),
      invalidatesTags: ["storagePhotos"],
      transformResponse: (response: IPhoto, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    rotateStoragePhoto: build.mutation<
      IPhoto,
      {
        photoId: number;
        rotation: number;
      }
    >({
      query: ({ photoId, rotation }) => {
        return {
          url: `photos/${photoId}/rotate`,
          method: "POST",
          data: {
            rotation,
          },
          timeout: 60000, // 60 seconds timeout for image processing
        };
      },
      invalidatesTags: ["storagePhotos"],
    }),
    // New endpoint for global searches without user ID filter
    getGlobalStoragesRequest: build.query<
      IDataResponse<IStorage>,
      {
        search?: string;
        status?: StorageStatus[];
        skip?: number;
        limit?: number;
      }
    >({
      query: ({
        search,
        status,
        skip = 0,
        limit = 5,
      }) => ({
        url: `storages?$skip=${skip}&$limit=${limit}${search && search !== "" ? `&q=${search}` : ``}${status ? status.map((item, index) => `&status[${index}]=${item}`).join("") : ""}&$sort[createdAt]=-1`,
        method: "get",
      }),
      transformResponse: (response: IDataResponse<IStorage>, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    
    getStoragesStatsRequest: build.query<
      IStorageStats,
      {
        userId: number;
        status: StorageStatus[];
      }
    >({
      query: ({ userId, status }) => ({
        url: `storage-statistics?userId=${userId}${status ? status.map((item, index) => `&storageStatus[${index}]=${item}`).join("") : ""}`,
        method: "get",
      }),
      transformResponse: (response: IStorageStats, meta, arg) => response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
    getStorageItem: build.query<
      IDataResponse<IStorage>,
      {
        storageId: number;
      }
    >({
      query: ({ storageId }) => ({
        url: `storages?id=${storageId}&$limit=50`,
        method: "get",
      }),
      transformResponse: (response: IDataResponse<IStorage>, meta, arg) =>
        response,
      transformErrorResponse: (response, meta, arg) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useDeleteUserCustomFieldMutation,
  useGetUserCustomFieldsQuery,
  useCreateUserCustomFieldMutation, 
  useUpdateUserCustomFieldMutation, 
  useGetAllStoragesRequestQuery,
  useGetStoragesRequestQuery,
  useLazyGetStoragesRequestQuery, // Added lazy query hook for fetching all storages
  useGetGlobalStoragesRequestQuery,
  useLazyGetGlobalStoragesRequestQuery, // Added lazy query hook for global searches without user ID
  useGetStoragePartsRequestQuery,
  useLazyGetStoragePartsRequestQuery,
  useGetStoragesStatsRequestQuery,
  useGetStoragesPhotosRequestQuery,
  useLazyGetStoragesPhotosRequestQuery,
  useSetStoragesPhotoAsDefaultRequestMutation,
  useRotateStoragePhotoMutation,
  useGetStorageItemQuery,
  useLazyGetStorageItemQuery,
  useUpdateStorageNameMutation, 
  endpoints,
} = extendedApi;
